import { defineStore } from 'pinia'
import { ref } from 'vue'

import i18n from '@/libs/i18n'
import { ADMIN_LOCALE_KEY, resolveInitialLocale } from '@/libs/i18n/languages'
import { setValidationLocale } from '@/libs/veeValidate'

export interface LoggedInUserData {
    id:        string
    name:      string
    username:  string
    role:      string
    email?:    string
    language?: string
}

export const useAppStore = defineStore('app', () => {
    const JWT          = ref(localStorage.getItem('JWT') || '')
    const locale       = ref(resolveInitialLocale())
    const windowWidth  = ref(window.innerWidth)
    const loaderActive = ref(0)
    const userData     = ref<LoggedInUserData>()

    const getJWT         = () => JWT.value
    const isLoggedIn     = () => !!JWT.value
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile       = () => windowWidth.value < 1024

    // Sets a JWT cookie so that API requests loaded via <a href> are authenticated.
    const setJWTCookie = (jwt: string) => {
        if (jwt) {
            document.cookie = `JWT=${jwt}; path=/; SameSite=Lax`
        } else {
            document.cookie = 'JWT=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax'
        }
    }

    setJWTCookie(JWT.value)

    const setLogin = (jwt: string) => {
        localStorage.setItem('JWT', jwt)
        JWT.value = jwt
        setJWTCookie(jwt)
    }

    const setLogout = () => {
        localStorage.removeItem('JWT')
        JWT.value = ''
        userData.value = undefined
        setJWTCookie('')
    }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => loaderActive.value = Math.max(0, loaderActive.value - 1)

    // Backstop against a leaked counter. Today loaderActive only drives the
    // :loading state of individual submit buttons (Login, ProfileDialog) — the
    // console renders no full-screen blocking overlay from it — so a leak cannot
    // by itself swallow clicks. resetLoading() is called from router.afterEach so
    // that a startLoading() whose stopLoading() was skipped by a throw can never
    // pin a spinner (or a future overlay) across navigations.
    const resetLoading = () => loaderActive.value = 0

    // applyLocale switches the console UI only, WITHOUT touching the stored
    // preference. It exists for the login page, which clamps a language chosen on
    // the public pages (up to 24 EU locales) down to the console's sl/en surface —
    // clamping must not overwrite the visitor's public-page choice.
    const applyLocale = (newLocale: string) => {
        locale.value = newLocale
        ;(i18n.global.locale as { value: string }).value = newLocale
        // Validation messages come from vee-validate's own catalog, so it has to
        // follow the UI language too.
        setValidationLocale(newLocale)
    }

    // changeLocale persists the console's OWN language under ADMIN_LOCALE_KEY. The
    // public pages keep their (up to 24 EU locales) choice under a separate key, so
    // neither surface can overwrite the other's language.
    const changeLocale = (newLocale: string) => {
        applyLocale(newLocale)
        localStorage.setItem(ADMIN_LOCALE_KEY, newLocale)
    }

    return {
        JWT, locale, userData, loaderActive, windowWidth,
        getJWT, isLoggedIn, isLoaderActive, isMobile,
        setLogin, setLogout, startLoading, stopLoading, resetLoading, applyLocale, changeLocale,
    }
})
